import { lazy, memo, Suspense, useCallback, useEffect, useState } from 'react';

import {
  bridgeAtom,
  chatIdAtom,
  isLoadingMessageAtom,
  isMlAnsweredAtom,
  isOpenChatAtom,
  isResetChatAtom,
  isUnauthorizedAtom,
  isUserAnsweredAtom,
  messagesAtom,
  messageTimeoutAtom,
  mlVersionAtom,
  requestsQueueAtom,
  sessionAtom,
  sessionFinishTimeAtom,
  useAtom,
  useReset,
  useSetValue,
  useValue,
} from '@/store';

import { AppEvents } from '@/utils';

import { request, useStompClient, useToken } from '@/modules/api';
import { useEvent } from '@/modules/common';
import { useBehaviour } from '@/modules/context';
import {
  MessageReasonType,
  MessageSenderType,
  MessageType,
  ModelActionType,
  PageType,
  SendToModelStatusType,
} from '@/modules/models';

import { useMessageTimeout } from '../../data';

const Layout = lazy(() => import('../layout').then((module) => ({ default: module.Layout })));

function parseJwt(token: string) {
  const base64Url = token.split('.')[1];
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  const jsonPayload = decodeURIComponent(
    window
      .atob(base64)
      .split('')
      .map(function (c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      })
      .join('')
  );

  return JSON.parse(jsonPayload);
}

export const DataLayer = memo(
  () => {
    const [token] = useToken();

    const [isRequestsQueueFetching, setIsRequestsQueueFetching] = useState(false);

    const { toggleChat } = useBehaviour();

    const [isOpenChat, setIsOpenChat] = useAtom(isOpenChatAtom);
    const [requestsQueue, setRequestsQueue] = useAtom(requestsQueueAtom);
    const [messages, setMessages] = useAtom(messagesAtom);

    const isUnauthorized = useValue(isUnauthorizedAtom);
    const chatId = useValue(chatIdAtom);
    const session = useValue(sessionAtom);
    const { onGetSuggestion, onSentSuggestion, onToggleChat, onResetChat } = useValue(bridgeAtom);

    const setIsMlAnswered = useSetValue(isMlAnsweredAtom);
    const setIsUserAnswered = useSetValue(isUserAnsweredAtom);
    const setResetChat = useSetValue(isResetChatAtom);
    const setMessageTimeout = useSetValue(messageTimeoutAtom);
    const setIsLoadingMessage = useSetValue(isLoadingMessageAtom);
    const setMlVersion = useSetValue(mlVersionAtom);

    const resetChatIdAtom = useReset(chatIdAtom);
    const resetSessionFinishTime = useReset(sessionFinishTimeAtom);

    useMessageTimeout();

    const resetChat = useCallback(() => {
      setResetChat(true);
      resetChatIdAtom();
      setIsOpenChat(false);
      resetSessionFinishTime();
      setMessages([]);

      // do not send a timeout message if chat is closed
      setMessageTimeout(-1);
      onResetChat?.();
    }, [
      resetChatIdAtom,
      setIsOpenChat,
      resetSessionFinishTime,
      setResetChat,
      setMessages,
      setMessageTimeout,
      onResetChat,
    ]);

    useEvent({ name: AppEvents.RESET_CHAT, handler: resetChat });

    const updateChatHistory = useCallback(
      (newMessage: MessageType) => {
        if (newMessage?.action === ModelActionType.NONE) {
          return;
        }

        // TODO: need to refactor this
        setMessages((prev) => {
          const messageExists = prev.some(
            (message: MessageType) =>
              (message.id === newMessage.id && message.role === newMessage.role) ||
              (newMessage.clientCorrelationId != null && message.clientCorrelationId === newMessage.clientCorrelationId)
          );

          if (messageExists) {
            return prev.map((message) =>
              message.id === newMessage.id ||
              (newMessage.clientCorrelationId != null && message.clientCorrelationId === newMessage.clientCorrelationId)
                ? newMessage
                : message
            );
          } else {
            return [...prev, newMessage];
          }
        });
      },
      [setMessages]
    );

    const onMessageReceived = useCallback(
      (newMessage: MessageType) => {
        if (
          newMessage.reason === MessageReasonType.INITIALIZATION &&
          newMessage.page_type === PageType.ADD_TO_CART_SUGGESTION &&
          newMessage.status === SendToModelStatusType.SUCCESS &&
          newMessage.role === MessageSenderType.USER
        ) {
          onSentSuggestion?.(newMessage.id as number);
        }

        if (!chatId || !newMessage.chatId || newMessage.chatId !== chatId || !newMessage.content) {
          return;
        }

        if (
          newMessage.reason === MessageReasonType.MODEL_RESPONSE &&
          newMessage.pageType === PageType.ADD_TO_CART_SUGGESTION &&
          newMessage.role === MessageSenderType.ASSISTANT
        ) {
          onGetSuggestion?.({
            replyMessageId: newMessage.replyMessageId as number,
            content: newMessage.contentNew,
          });
        }

        if (newMessage.mlVersion) {
          setMlVersion(newMessage.mlVersion);
        }

        switch (newMessage.reason) {
          case MessageReasonType.USER_INPUT: {
            updateChatHistory(newMessage);
            return;
          }
          case MessageReasonType.MODEL_RESPONSE: {
            if (newMessage.action === ModelActionType.HIBERNATE) {
              toggleChat();
              return;
            }

            setIsLoadingMessage(false);

            const newMessageTimeout = newMessage.timeout || -1;

            setMessageTimeout(newMessageTimeout);
            updateChatHistory(newMessage);
          }
        }
      },
      [
        chatId,
        setIsLoadingMessage,
        setMessageTimeout,
        toggleChat,
        updateChatHistory,
        setMlVersion,
        onGetSuggestion,
        onSentSuggestion,
      ]
    );

    const onHistoryReceived = useCallback(
      (messages: MessageType[]) => {
        const lastMessage = messages[messages.length - 1];
        const messageWithMlVersion = messages.findLast((mess) => mess.mlVersion);
        const mlVersion = messageWithMlVersion?.mlVersion;
        if (mlVersion) {
          setMlVersion(mlVersion);
        }

        if (
          lastMessage?.reason === MessageReasonType.MODEL_RESPONSE &&
          (lastMessage.action === ModelActionType.SAY || lastMessage.action === ModelActionType.NONE)
        ) {
          setMessageTimeout(lastMessage.timeout || -1);
          setIsLoadingMessage(false);
        }

        setMessages(messages.filter((m) => m.action === null || m.action === ModelActionType.SAY));
      },
      [setMessages, setIsLoadingMessage, setMessageTimeout, setMlVersion]
    );

    const { sendMessage, initConversation, isSubscribed } = useStompClient({
      onMessageReceived,
      onHistoryReceived,
      onReset: resetChat,
    });

    const onSendMessage = useCallback(
      async (message: MessageType) => {
        setIsLoadingMessage(true);

        const response = await sendMessage(message);

        if (response?.error) {
          setIsLoadingMessage(false);
        }

        return response;
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [sendMessage]
    );

    const hasSession = Boolean(session);

    useEffect(() => {
      if (!isOpenChat) {
        return;
      }

      if (!isSubscribed) {
        setIsLoadingMessage(true);
      }

      if (isSubscribed && hasSession) {
        initConversation();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isSubscribed, isOpenChat, hasSession]);

    useEffect(() => {
      const mlMessages = messages.filter(
        ({ reason, role }) => reason === MessageReasonType.MODEL_RESPONSE && role === MessageSenderType.ASSISTANT
      );
      const userMessages = messages.filter(
        ({ reason, role }) => reason === MessageReasonType.USER_INPUT && role === MessageSenderType.USER
      );
      setIsMlAnswered(mlMessages.length >= 2);
      setIsUserAnswered(userMessages.length > 0);
    }, [messages, setIsMlAnswered, setIsUserAnswered]);

    useEffect(() => {
      if (!isRequestsQueueFetching && !isUnauthorized && requestsQueue.length > 0 && token) {
        setIsRequestsQueueFetching(true);
        (async () => {
          for (const req of requestsQueue) {
            try {
              await request.request(req.url, {
                method: req.method,
                body: req.body ? JSON.parse(req.body) : undefined,
                headers: req.headers,
              });
            } catch (err) {
              console.error('Failed to retry request:', req.url, err);
            }
          }

          setRequestsQueue([]);
          setIsRequestsQueueFetching(false);
        })();
      }
    }, [isRequestsQueueFetching, isUnauthorized, token, requestsQueue, setRequestsQueue]);

    // This effect is used for analytics. By chatId we can associate the user and his cart.
    const chatIdFromToken = token ? parseJwt(token).CHAT_ID : null;
    useEffect(() => {
      // @ts-ignore
      const shopifyRootUrl = window.Shopify?.routes?.root ?? '/';
      fetch(shopifyRootUrl + 'cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          attributes: {
            chat_id: `${chatIdFromToken}`,
          },
        }),
      }).catch((error) => {
        console.error('Error sending chat id:', error);
      });
    }, [chatIdFromToken]);

    useEffect(() => {
      onToggleChat?.(isOpenChat);
    }, [isOpenChat, onToggleChat]);

    if (!token) {
      return null;
    }

    return (
      <Suspense fallback={null}>
        <Layout onSendMessage={onSendMessage} />
      </Suspense>
    );
  },
  () => true
);
